import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Printer, Pencil, Trash2, Sparkles, Download } from "lucide-react";
import { MessaggioDialog } from "../components/MessaggioDialog";
import { downloadPdf } from "../lib/pdf";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { fmtDate } from "../lib/format";
import { Targa } from "../components/Targa";
import { StatoBadge, TipoBadge } from "../components/Badges";
import { GaranziaDialog } from "../components/GaranziaDialog";
import { ReclamoDialog } from "../components/ReclamoDialog";
import { ClienteCard, VeicoloCard, CoperturaCard, ReclamiSection } from "../components/GaranziaCards";
import { Button } from "../components/ui/button";

const totaleApprovati = (reclami = []) =>
  reclami.filter((r) => ["approvato", "chiuso"].includes(r.stato)).reduce((s, r) => s + r.costo_ricambi + r.costo_manodopera, 0);

export default function GaranziaDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [g, setG] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [msgOpen, setMsgOpen] = useState(false);
  const [rec, setRec] = useState({ open: false, item: null });

  const load = () => api.get(`/garanzie/${id}`).then((r) => setG(r.data)).catch(() => nav("/garanzie"));
  useEffect(() => { load(); }, [id]); // eslint-disable-line

  const remove = async () => {
    if (!window.confirm(`Eliminare la garanzia ${g.numero} e i relativi reclami?`)) return;
    try { await api.delete(`/garanzie/${id}`); toast.success("Garanzia eliminata"); nav("/garanzie"); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const totaleInterventi = useMemo(() => totaleApprovati(g?.reclami), [g]);
  const contatti = useMemo(() => [g?.cliente?.telefono, g?.cliente?.email].filter(Boolean).join(" · "), [g]);
  const indirizzo = useMemo(() => [g?.cliente?.indirizzo, g?.cliente?.cap, g?.cliente?.citta].filter(Boolean).join(", "), [g]);

  if (!g) return <div className="text-sm text-muted-foreground">Caricamento…</div>;
  const c = g.cliente || {}, v = g.veicolo || {};

  return (
    <div>
      <Link to="/garanzie" className="mb-4 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="back-to-garanzie"><ArrowLeft className="h-4 w-4" /> Registro garanzie</Link>
      <div className="rise mb-6 overflow-hidden rounded-md border border-[#B08D57]/30 bg-[#0F1B2D] text-white">
        <div className="grid-bg flex flex-col gap-5 p-6 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="mb-2 flex flex-wrap items-center gap-2"><TipoBadge g={g} /><StatoBadge stato={g.stato} testId="detail-stato" /></div>
            <h1 className="font-mono text-2xl font-bold tracking-wider sm:text-3xl" data-testid="detail-numero">{g.numero}</h1>
            <div className="mt-1 text-sm text-slate-300">{g.piano} · {fmtDate(g.data_inizio)} → {fmtDate(g.data_fine)}
              {g.giorni_rimanenti >= 0 && <span className="text-[#D4B98A]"> · {g.giorni_rimanenti} giorni rimanenti</span>}</div>
          </div>
          <Targa value={v.targa} size="lg" testId="detail-targa" />
        </div>
        <div className="flex flex-wrap gap-2 border-t border-white/10 bg-black/20 px-6 py-3">
          <Button asChild size="sm" className="bg-[#B08D57] hover:bg-[#96764A]" data-testid="print-certificato-btn"><Link to={`/garanzie/${id}/certificato`}><Printer className="mr-1 h-4 w-4" /> Stampa contratto</Link></Button>
          <Button size="sm" variant="secondary" onClick={() => downloadPdf(id, g.numero)} data-testid="download-pdf-btn"><Download className="mr-1 h-4 w-4" /> Scarica PDF</Button>
          <Button size="sm" variant="secondary" onClick={() => setMsgOpen(true)} data-testid="genera-messaggio-btn"><Sparkles className="mr-1 h-4 w-4 text-[#B08D57]" /> Messaggio AI</Button>
          <Button size="sm" variant="secondary" onClick={() => setEditOpen(true)} data-testid="edit-garanzia-btn"><Pencil className="mr-1 h-4 w-4" /> Modifica</Button>
          {user?.role === "admin" && <Button size="sm" variant="destructive" onClick={remove} data-testid="delete-garanzia-btn"><Trash2 className="mr-1 h-4 w-4" /> Elimina</Button>}
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <ClienteCard c={c} contatti={contatti} indirizzo={indirizzo} />
        <VeicoloCard v={v} kmInizio={g.km_inizio} />
        <CoperturaCard g={g} />
      </div>

      <ReclamiSection reclami={g.reclami} totale={totaleInterventi}
        onNew={() => setRec({ open: true, item: null })} onOpen={(r) => setRec({ open: true, item: r })} />

      {msgOpen && <MessaggioDialog open={msgOpen} onClose={() => setMsgOpen(false)} garanzia={g} />}
      <GaranziaDialog open={editOpen} onClose={() => setEditOpen(false)} initial={g} onSaved={load} />
      <ReclamoDialog open={rec.open} onClose={() => setRec({ open: false, item: null })} initial={rec.item} garanziaId={id} onSaved={load} />
    </div>
  );
}
